<template>
  <PopupContent
    v-show="showPopup"
    class="visualisation-popup"
    :style="popupStyle"
    :popup-details="popupDetails"
    @updated="updatePopupCardSize"
  />
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'

import PopupContent from '@/components/public/Popup/PopupContent.vue'
import { VisualisationPopupDetails } from '@/types'
import _clamp from 'lodash/clamp'
import _round from 'lodash/round'

const POPUP_CARD_WIDTH = 450
const SHAPE_HORIZONTAL_MARGIN = 20
const VERTICAL_OFFSET = 50

const components = {
  PopupContent,
}

const props = {
  popupDetails: {
    type: Object as PropType<VisualisationPopupDetails | null>,
  },
}

// eslint-disable-next-line no-use-before-define
@Component<VisualisationPopup>({ components })
export default class VisualisationPopup extends Vue.extend({ props }) {
  popupCardHeight: number = 0

  get showPopup() {
    return this.popupDetails !== null
  }

  get mainPanelHeight() {
    return this.$typedStore.public.size.mainPanelDimensions.height
  }

  get popupStyle() {
    return {
      top: `${this.position.top}px`,
      left: `${this.position.left}px`,
      width: `${POPUP_CARD_WIDTH}px`,
      transform: `scale(${this.transformCardScale})`,
      transformOrigin: 'left top',
    }
  }

  get maxAvailableContainerHeight() {
    // 90% of the available height so that the popup should not touch the top or bottom edges
    return this.mainPanelHeight * 0.9
  }

  get transformCardScale() {
    // https://css-tricks.com/scaled-proportional-blocks-with-css-and-javascript
    // The scale is the available length divided by the content length
    // e.g. availableHeight / contentHeight
    if (
      this.maxAvailableContainerHeight &&
      this.popupCardHeight &&
      this.popupCardHeight > this.maxAvailableContainerHeight
    ) {
      const scale = this.maxAvailableContainerHeight / this.popupCardHeight
      return Math.min(scale, 1)
    }
    return 1
  }

  get position() {
    if (!this.popupDetails) {
      return { left: 0, top: 0 }
    }

    return {
      left: this.leftPosition,
      top: this.topPosition,
    }
  }

  get leftPosition() {
    if (!this.popupDetails) {
      return 0
    }

    const scaledPopupWidth = POPUP_CARD_WIDTH * this.transformCardScale
    const { leftBuffer, entityTopLeft: shapeTopLeft } = this.popupDetails
    const horizontalBuffer = 10 + (leftBuffer ?? 0)

    const left = shapeTopLeft.x - scaledPopupWidth - horizontalBuffer
    const minLeft = -scaledPopupWidth + SHAPE_HORIZONTAL_MARGIN

    return Math.max(left, minLeft)
  }

  get topPosition() {
    if (!this.popupDetails) {
      return 0
    }

    const scaledPopupHeight = _round(this.popupCardHeight * this.transformCardScale, 1)
    const minTop = (this.mainPanelHeight - this.maxAvailableContainerHeight) / 2
    const maxTop = this.mainPanelHeight - scaledPopupHeight - minTop
    const shapeCenter = this.popupDetails.entityCenter.y

    let top
    if (scaledPopupHeight === this.maxAvailableContainerHeight) {
      // If the card takes up all available height, center it vertically
      top = (this.mainPanelHeight - scaledPopupHeight) / 2
    } else {
      // Determine whether to display above or below the bubble
      const displayBelow = shapeCenter < this.mainPanelHeight / 2
      top = displayBelow ? shapeCenter - VERTICAL_OFFSET : shapeCenter + VERTICAL_OFFSET
    }

    return _clamp(top, minTop, maxTop)
  }

  updatePopupCardSize(newValue: { clientHeight: number }) {
    this.popupCardHeight = newValue.clientHeight
  }
}
</script>

<style scoped>
.visualisation-popup {
  position: absolute;
  z-index: 5;
  width: 450px;
  opacity: 0.97;
}
</style>
